import React, { useContext, useState } from "react";
import * as yup from "yup";
import { DefaultModalForm, DeleteModal, ModalForm } from "collections/Modal";
import { useTranslation } from "utilities/i18n";
import { useDeleteTenantMutation } from "generated/graphql";
import { DISMISS, Input, useNotification } from "collections";
import { CancelButton, SubmitButton } from "elements";
import { Form } from "collections/Form/Form";
import { useHistory, useQueryParamString } from "polyrouter";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { AccountForTable } from "routes/Admin/Accounts/Accounts";
import { PayloadAC } from "typesafe-actions";

interface DeleteAccountModalProps {
  accounts: AccountForTable[];
  onComplete: () => void;
  addToBlackList: PayloadAC<"AddToBlackList", string[]>;
}

export const ConfirmationFormSchema = yup.object().shape({
  confirmField: yup.string().ensure()
});

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  accounts,
  onComplete,
  addToBlackList
}) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const { tenant } = useContext(SelectedTenantContext);
  const [urlSearchTerm] = useQueryParamString<"searchTerm">("searchTerm");
  const searchPart = urlSearchTerm ? `searchTerm=${urlSearchTerm}&` : "";
  const tenantId = tenant?.id ?? "";
  const { replace } = useHistory();
  const [isConfirmScreen, setIsConfirmScreen] = useState(false);
  const [, deleteTenant] = useDeleteTenantMutation();

  const returnTo = () =>
    replace(`/admin/accounts?${searchPart}tenantId=${tenantId}`);

  const onSubmit = async () => {
    let error = undefined;
    const removeIds = accounts.map(account => account.id);

    for (const { id, name } of accounts) {
      const { data, error: e } = await deleteTenant({ tenantId: id });
      if (e || data?.deleteTenant.id !== id) {
        error = e;
        addNotification({
          type: "error",
          message: t("ACCOUNT_DELETE_ERROR", { name }),
          dismiss: DISMISS.USER_ONLY,
          persistThroughNav: true
        });
      }
    }

    if (!error) {
      addNotification({
        type: "success",
        message: t("ACCOUNT_DELETE_SUCCESS", {
          name: accounts[0].name,
          count: accounts.length
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });

      addToBlackList(removeIds);
    } else {
      addNotification({
        type: "error",
        message: t("ACCOUNT_DELETE_ERROR", {
          name: `${
            accounts.length > 1
              ? "Some accounts have "
              : accounts[0].name + " has "
          } failed to delete`,
          count: accounts.length
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }

    returnTo();
    onComplete();
  };

  if (accounts.length === 0) returnTo();

  if (
    accounts.find(
      ({ deviceCount }) =>
        deviceCount === undefined ||
        (typeof deviceCount === "number" && deviceCount > 0)
    )
  )
    return (
      <DefaultModalForm
        title={t(
          accounts.length === 1
            ? "ACCOUNT_DELETE_TITLE"
            : "ACCOUNTS_DELETE_TITLE"
        )}
        onSubmit={returnTo}
        name="RemoveAccount"
      >
        <div data-testid="delete-blocked-modal">
          <div className="pb-1 small-text" data-testid="confirmation">
            {t("ACCOUNT_DELETE_BLOCKED_REASON")}
          </div>

          <table className="table device-count-table">
            <thead>
              <tr>
                <th className="p-5">
                  <label>{t("ACCOUNT_NAME")}</label>{" "}
                </th>
                <th className="p-5">
                  <label>{t("DEVICE_COUNT")}</label>
                </th>
              </tr>
            </thead>
            <tbody>
              {accounts
                .filter(({ deviceCount }) => deviceCount !== 0)
                .map(({ name, deviceCount }, i) => (
                  <tr key={i} className="list-item">
                    <td className="p-5">{name}</td>
                    <td className="p-5">
                      {typeof deviceCount === "number" ? deviceCount : "N/A"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <div className="btn-container pt-2">
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="acknowledgement-button"
            >
              {t("I_UNDERSTAND")}
            </button>
          </div>
        </div>
      </DefaultModalForm>
    );

  if (!isConfirmScreen)
    return (
      <DeleteModal
        entity={t("ACCOUNT")}
        confirmationString={t("ACCOUNT_DELETE_DETAILS")}
        onDelete={() => setIsConfirmScreen(true)}
        name="DeleteAccount"
      />
    );

  return (
    <DeleteConfirmationModal
      name="ConfirmRemoveAccount"
      accounts={accounts}
      title={t("ACCOUNT_DELETE_CONFIRMATION_TITLE")}
      onSubmit={onSubmit}
      onCancel={returnTo}
    />
  );
};

export const DeleteConfirmationModal: React.FC<{
  name: string;
  accounts: AccountForTable[];
  fetching?: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  title: string;
}> = ({ name, accounts, fetching, onCancel, onSubmit, title }) => {
  const { t } = useTranslation();

  return (
    <ModalForm
      name={name}
      title={title}
      validationSchema={ConfirmationFormSchema}
      initialValues={{ confirmField: "" }}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting }) => {
        return (
          <div data-testid="confirmation-modal">
            <Form autoComplete="off">
              <div className="small-text" data-testid="confirmation">
                {t("DELETE_CONFIRMATION_DETAILS")}
              </div>
              <table className="table device-count-table">
                <thead>
                  <tr>
                    <th className="p-5">
                      <label>{t("ACCOUNT_NAME")}</label>{" "}
                    </th>
                    <th className="p-5">
                      <label>{t("DEVICE_COUNT")}</label>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(({ name, deviceCount }, i) => (
                    <tr key={i} className="list-item">
                      <td className="p-5">{name}</td>
                      <td className="p-5">
                        {typeof deviceCount === "number" ? deviceCount : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pt-15 mh-4">
                <Input
                  name="confirmField"
                  placeholder={t("DELETE").toUpperCase()}
                  type="text"
                />
              </div>
              <div className="btn-container pt-2">
                <CancelButton onClick={onCancel} disabled={isSubmitting} />
                <SubmitButton
                  isSubmitting={isSubmitting || !!fetching}
                  testId="confirm"
                  disabled={values.confirmField !== t("DELETE").toUpperCase()}
                  labelKey="CONFIRM"
                  submittingLabelKey="DELETING"
                />
              </div>
            </Form>
          </div>
        );
      }}
    </ModalForm>
  );
};

export default DeleteAccountModal;
